import { useImperativeHandle, useRef } from 'react';

import BybitForm from './BybitForm';
import { BybitFormProps } from './BybitForm';
import BybitTable from './BybitTable';
import { BybitTableProps } from './BybitTable';
import {
  BybitFormRef,
  BybitSearchListRef,
  MockData,
} from './utils/commonInterface';
import { useSearchList } from './utils/useSearchList';

type SearchListCompProps<
  RecordType,
  FormValues = any,
  SearchValues = FormValues,
> = Partial<
  Omit<BybitFormProps<FormValues, SearchValues>, 'onFormRef' | 'handleSearch'>
> &
  Omit<BybitTableProps<RecordType>, 'dataSource' | 'loading'>;

interface SearchListProps<
  RecordType,
  FormValues extends object = any,
  SearchValues = FormValues,
> extends SearchListCompProps<RecordType, FormValues, SearchValues> {
  searchListRef?: React.MutableRefObject<
    BybitSearchListRef<RecordType, FormValues, SearchValues> | undefined
  >;
  dataSourceKey?: string;
  hideSearch?: boolean;
  hideMock?: boolean;
  otherSearchValues?: object;
  isSendRequest?: boolean;
  mockData?: MockData<RecordType>;
  title?: () => JSX.Element;
  getApi: (params?: SearchValues) => any;
  beforeSearch?: (values: FormValues) => SearchValues;
  doSearch?: (...params: any[]) => void;
  onSuccess?: (...params: any[]) => void;
  onError?: (...params: any[]) => void;
  className?: string;
}

function SearchListContainer<
  RecordType extends object = any,
  FormValues extends object = any,
  SearchValues = FormValues,
>(opt: SearchListProps<RecordType, FormValues, SearchValues>): JSX.Element {
  const {
    className,
    rowKey,
    searchListRef,
    hideSearch = false,
    formType,
    columns,
    tableProps,
    formProps,
    configData = { query: [] },
    initialValues,
    otherSearchValues,
    defaultPageSize = 10,
    isSendRequest = true,
    mockData,
    hideMock,
    dataSourceKey = 'result.data',
    onSuccess,
    onError,
    getApi,
    doSearch,
    title,
    beforeSearch,
    footer,
  } = opt;
  const paginationRef: any = useRef();
  const formRef = useRef<BybitFormRef<FormValues, SearchValues>>();

  const getPageNoFn = () => {
    if (paginationRef.current && paginationRef.current.resetPageNo) {
      return paginationRef.current;
    }
  };
  const resetPageNo = () => {
    const pageInfo = getPageNoFn();
    if (pageInfo && pageInfo.resetPageNo) {
      pageInfo.resetPageNo();
    }
  };
  const {
    dataSource,
    isLoading,
    searchParams,
    ajax,
    pageParams,
    turnPages,
    setDataList,
  } = useSearchList<RecordType, FormValues, SearchValues>({
    otherSearchValues,
    initialValues,
    isSendRequest,
    mockData,
    defaultPageSize,
    hideMock,
    dataSourceKey,
    getApi,
    getPageNoFn,
    onSuccess,
    onError,
  });

  const getFormDetail = (): BybitFormRef | undefined => {
    if (formRef && formRef.current) {
      return formRef.current;
    }
  };

  const getDataSource = () => {
    return dataSource;
  };

  useImperativeHandle(searchListRef, () => ({
    ...(getFormDetail() as BybitFormRef),
    resetPageNo,
    refreshList,
    setDataList,
    getDataSource,
  }));

  const handleSearch = async (...params: any[]) => {
    if (doSearch) {
      doSearch(...params);
      return;
    }
    await ajax(...params);
    if (!footer) {
      resetPageNo();
    }
  };
  const refreshList = (values: Partial<SearchValues> = {}) => {
    handleSearch({ ...searchParams, ...values });
  };
  const getTableProps = () => {
    const tableConf: BybitTableProps<RecordType> = {
      className,
      rowKey,
      dataSource,
      columns,
      paginationRef,
      loading: isLoading,
      tableProps,
      defaultPageSize,
      pageParams,
      title,
      footer,
      turnPages,
    };
    return tableConf;
  };

  const getFormProps = () => {
    const formConf = {
      formType,
      configData,
      onFormRef: formRef,
      pagination: false,
      initialValues,
      formProps,
      handleSearch,
      beforeSearch,
    };
    return formConf;
  };
  return (
    <div>
      {!hideSearch && (
        <BybitForm<FormValues, SearchValues> {...getFormProps()} />
      )}
      <BybitTable<RecordType> {...getTableProps()} />
    </div>
  );
}

export default SearchListContainer;
